import { useState, useMemo } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import { Add, Edit, ToggleOn, ToggleOff, Savings } from '@mui/icons-material';
import {
  useConceptosAporteAll,
  useToggleConceptoAporte,
} from '../../hooks/useConceptosAporteAdmin';
import ConceptoAporteDialog from './ConceptoAporteDialog';
import type { ConceptoAporte } from '../../types/afiliado';

export default function ConceptosAporteList() {
  const { data: conceptos = [], isLoading, isError, error } = useConceptosAporteAll(true);
  const toggle = useToggleConceptoAporte();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ConceptoAporte | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (concepto: ConceptoAporte) => {
    setEditing(concepto);
    setDialogOpen(true);
  };

  const handleToggle = async (c: ConceptoAporte) => {
    const accion = c.activo ? 'desactivar' : 'activar';
    if (!confirm(`Confirmas ${accion} el concepto "${c.descripcion}"?`)) return;
    await toggle.mutateAsync({ id: c.id, activo: !c.activo });
  };

  const automaticos = conceptos.filter((c) => !c.opcional);
  const opcionales = conceptos.filter((c) => c.opcional);

  const columns = useMemo<GridColDef<ConceptoAporte>[]>(
    () => [
      {
        field: 'codigo',
        headerName: 'Codigo',
        width: 160,
        renderCell: (p) => (
          <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
            {p.value}
          </Typography>
        ),
      },
      { field: 'descripcion', headerName: 'Descripcion', flex: 1, minWidth: 200 },
      {
        field: 'porcentaje',
        headerName: 'Porcentaje',
        width: 110,
        renderCell: (p) => (
          <Typography variant="body2" fontWeight={600}>
            {Number(p.value).toFixed(2)}%
          </Typography>
        ),
      },
      {
        field: 'opcional',
        headerName: 'Tipo',
        width: 130,
        renderCell: (p: GridRenderCellParams<ConceptoAporte>) =>
          p.value ? (
            <Chip label="Opcional" size="small" color="info" variant="outlined" />
          ) : (
            <Chip label="Automatico" size="small" color="primary" />
          ),
      },
      {
        field: 'aplica_afiliados',
        headerName: 'Afiliados',
        width: 110,
        renderCell: (p) => (p.value ? 'Si' : '—'),
      },
      {
        field: 'aplica_no_afiliados',
        headerName: 'No afiliados',
        width: 120,
        renderCell: (p) => (p.value ? 'Si' : '—'),
      },
      {
        field: 'activo',
        headerName: 'Estado',
        width: 110,
        renderCell: (p: GridRenderCellParams<ConceptoAporte>) => (
          <Chip
            label={p.value ? 'Activo' : 'Inactivo'}
            size="small"
            color={p.value ? 'success' : 'default'}
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'Acciones',
        width: 130,
        sortable: false,
        filterable: false,
        renderCell: (p: GridRenderCellParams<ConceptoAporte>) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => openEdit(p.row)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={p.row.activo ? 'Desactivar' : 'Activar'}>
              <IconButton
                size="small"
                color={p.row.activo ? 'warning' : 'success'}
                onClick={() => handleToggle(p.row)}
              >
                {p.row.activo ? (
                  <ToggleOn fontSize="small" />
                ) : (
                  <ToggleOff fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    []
  );

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Conceptos de aporte
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Catalogo de conceptos que se aplican en las declaraciones juradas
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
          Nuevo concepto
        </Button>
      </Stack>

      <Alert severity="info" icon={<Savings />} sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Conceptos automaticos</strong> ({automaticos.length}): se aplican
          a TODAS las DDJJ segun la condicion de afiliacion del empleado.
        </Typography>
        <Typography variant="body2">
          <strong>Conceptos opcionales</strong> ({opcionales.length}): solo se
          aplican si se asignan explicitamente al afiliado desde su detalle
          (tab "Aportes Extra").
        </Typography>
      </Alert>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar conceptos: {(error as Error)?.message ?? 'desconocido'}
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={conceptos}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          autoHeight
          initialState={{
            sorting: { sortModel: [{ field: 'opcional', sort: 'asc' }] },
          }}
          sx={{
            border: 0,
            '& .MuiDataGrid-columnHeaders': { bgcolor: 'background.default' },
          }}
          localeText={{
            noRowsLabel: 'No hay conceptos cargados',
            footerTotalRows: 'Total:',
            MuiTablePagination: {
              labelRowsPerPage: 'Por pagina:',
              labelDisplayedRows: ({ from, to, count }) => `${from}-${to} de ${count}`,
            },
          }}
        />
      </Card>

      <ConceptoAporteDialog
        open={dialogOpen}
        concepto={editing}
        onClose={() => setDialogOpen(false)}
      />
    </Box>
  );
}
